import { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Sparkles, ArrowUpRight, GraduationCap, Linkedin, Twitter, Instagram, Youtube, Facebook, Mail, Phone, MapPin, Menu, X, MessageCircle, FileDown, Download, FileText, BookOpen, Users, Eye } from "lucide-react";
import { useCms } from "@/lib/cms";
import { fontCss } from "@/pages/admin/CertificateDesigner";
import api, { BACKEND } from "@/lib/api";

/**
 * Single source of truth for whether/what company-name text renders in the
 * header — used identically by desktop, tablet, mobile top bar, and the
 * mobile drawer. Nothing is ever shown unless the admin has explicitly
 * turned on `header_company_name_visible`; company_name alone (which also
 * feeds SEO/JSON-LD elsewhere) is never enough to make text appear here.
 * `mobileOverride` lets the mobile-specific text field (if set) take
 * priority while still being gated by the same master toggle.
 */
function resolveHeaderCompanyName(site, mobileOverride = "") {
  if (!site?.header_company_name_visible) return "";
  const value = mobileOverride || site?.header_company_name || site?.company_name || "";
  return value.trim();
}

export const Logo = ({ className = "", previewSite = null }) => {
  const ctx = useCms();
  const site = previewSite || ctx.site;
  const name = resolveHeaderCompanyName(site);
  const logoW = site?.logo_width ?? 32;
  const logoH = site?.logo_height ?? 32;
  const logoRadius = site?.logo_radius ?? 8;
  const nameStyle = {
    fontSize: site?.company_name_font_size ? `${site.company_name_font_size}px` : undefined,
    fontWeight: site?.company_name_font_weight ?? 600,
    letterSpacing: site?.company_name_letter_spacing != null ? `${site.company_name_letter_spacing}em` : undefined,
    color: site?.company_name_color || "#0F172A",
    marginTop: site?.company_name_margin_top || undefined,
    marginBottom: site?.company_name_margin_bottom || undefined,
    marginLeft: site?.company_name_margin_left || undefined,
    marginRight: site?.company_name_margin_right || undefined,
    paddingTop: site?.company_name_padding_top || undefined,
    paddingRight: site?.company_name_padding_right || undefined,
    paddingBottom: site?.company_name_padding_bottom || undefined,
    paddingLeft: site?.company_name_padding_left || undefined,
    maxWidth: site?.company_name_max_width ? `${site.company_name_max_width}px` : undefined,
    textAlign: site?.company_name_text_align || undefined,
    textTransform: site?.company_name_text_transform || undefined,
    opacity: site?.company_name_opacity != null && site?.company_name_opacity !== 1 ? site.company_name_opacity : undefined,
    whiteSpace: "pre-line",
    transform: (site?.company_name_offset_x || site?.company_name_offset_y)
      ? `translate(${site?.company_name_offset_x || 0}px, ${site?.company_name_offset_y || 0}px)` : undefined,
  };
  const logoGap = site?.logo_name_gap ?? site?.logo_margin_right ?? 8;
  const logoScale = site?.logo_scale ?? 1;
  const wrapStyle = {
    marginTop: site?.logo_top_offset || undefined,
    marginBottom: site?.logo_bottom_offset || undefined,
    alignItems: site?.company_name_valign === "top" ? "flex-start" : site?.company_name_valign === "bottom" ? "flex-end" : "center",
    gap: `${logoGap}px`,
  };
  const logoBoxStyle = {
    width: logoW, height: logoH, borderRadius: logoRadius,
    marginLeft: site?.logo_left_spacing || undefined,
    transform: [
      logoScale !== 1 ? `scale(${logoScale})` : "",
      (site?.logo_offset_x || site?.logo_offset_y) ? `translate(${site?.logo_offset_x || 0}px, ${site?.logo_offset_y || 0}px)` : "",
    ].filter(Boolean).join(" ") || undefined,
  };
  return (
  <Link to="/" className={`flex min-w-0 group ${className}`} style={wrapStyle} data-testid="brand-logo">
    {site?.logo_url ? (
      <img
        src={site.logo_url.startsWith("http") ? site.logo_url : `${process.env.REACT_APP_BACKEND_URL}${site.logo_url}`}
        alt={name}
        className="shrink-0"
        style={{ ...logoBoxStyle, objectFit: "cover" }}
        key={site.logo_url}
      />
    ) : (
      <div className="flex items-center justify-center relative overflow-hidden shrink-0" style={{ ...logoBoxStyle, backgroundColor: "#0F172A" }}>
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-500 to-emerald-500 opacity-70" />
        <Sparkles className="w-4 h-4 text-white relative" strokeWidth={2.2} />
      </div>
    )}
    <div className="flex flex-col justify-center min-w-0 leading-tight">
      <span className="mnav-company-name font-display break-words" style={nameStyle}>{name}</span>
      {site?.tagline_show && site?.tagline && (
        <span className="break-words" style={{
          fontSize: `${site?.tagline_font_size ?? 11}px`,
          color: site?.tagline_color || "#94A3B8",
          fontWeight: site?.tagline_font_weight ?? 400,
          letterSpacing: site?.tagline_letter_spacing != null ? `${site.tagline_letter_spacing}em` : undefined,
        }}>{site.tagline}</span>
      )}
    </div>
  </Link>
  );
};

export const MarketingNav = () => {
  const { site } = useCms();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  // Separate mount vs visible so both open AND close can animate — drawerMounted
  // keeps the DOM node alive during the closing transition; drawerVisible drives
  // the CSS transform (on -> off triggers the slide-out).
  const [drawerMounted, setDrawerMounted] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const closeMobile = useCallback(() => setMobileOpen(false), []);

  // ── Viewport observer — auto-close drawer when crossing to desktop ────────
  // Uses matchMedia for a single, efficient, debounce-free listener that fires
  // only on actual breakpoint crossings (not every resize pixel).
  const bpValue = site?.mobile_breakpoint ?? 768;
  useEffect(() => {
    const mql = window.matchMedia(`(min-width: ${bpValue}px)`);
    const onChange = () => { if (mql.matches) setMobileOpen(false); };
    // Close immediately if already at desktop width (e.g. hot-reload, orientation)
    onChange();
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, [bpValue]);

  // ── Close drawer on route change (browser back/forward, programmatic navigate) ──
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  // Swipe-to-close — tracks a single touch across the drawer panel; a drag past
  // the threshold toward the drawer's "closed" edge (left for a left-docked
  // drawer, right for a right-docked one) closes it on release. Purely additive:
  // reuses closeMobile, no new open/close state.
  const touchRef = useRef(null);
  const onDrawerTouchStart = (e) => { touchRef.current = e.touches[0].clientX; };
  const onDrawerTouchEnd = (e, position) => {
    if (touchRef.current == null) return;
    const delta = e.changedTouches[0].clientX - touchRef.current;
    touchRef.current = null;
    const threshold = 60;
    const closingSwipe = position === "right" ? delta > threshold : delta < -threshold;
    if (closingSwipe) closeMobile();
  };

  // ESC key closes drawer
  useEffect(() => {
    if (!mobileOpen) return;
    const onKey = (e) => { if (e.key === "Escape") closeMobile(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [mobileOpen, closeMobile]);

  // Lock body scroll when drawer is open — always restore on unmount
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  // Drives the two-phase drawer animation described above.
  useEffect(() => {
    const speed = site?.mobile_menu_animation_speed ?? 220;
    if (mobileOpen) {
      setDrawerMounted(true);
      const raf = requestAnimationFrame(() => setDrawerVisible(true));
      return () => cancelAnimationFrame(raf);
    } else {
      setDrawerVisible(false);
      const t = setTimeout(() => setDrawerMounted(false), speed + 30);
      return () => clearTimeout(t);
    }
  }, [mobileOpen, site?.mobile_menu_animation_speed]);
  const DEFAULT_NAV = [
    { id: "n1", to: "/programs",      label: "Programs",           visible: true, device: "all", target: "_self" },
    { id: "n2", to: "/gallery",       label: "Gallery",            visible: true, device: "all", target: "_self" },
    { id: "n3", to: "/achievements",  label: "Achievements",       visible: true, device: "all", target: "_self" },
    { id: "n4", to: "/podcast",       label: "Podcast",            visible: true, device: "all", target: "_self" },
    { id: "n5", to: "/experts",       label: "Skilled Teachers",   visible: true, device: "all", target: "_self" },
    { id: "n6", to: "/about",         label: "About",              visible: true, device: "all", target: "_self" },
    { id: "n7", to: "/verify",        label: "Verify Certificate", visible: true, device: "all", target: "_self" },
    { id: "n8", to: "/contact",       label: "Contact",            visible: true, device: "all", target: "_self" },
  ];
  // Validate: every nav item must have a string `to` and `label` to be renderable.
  // Malformed entries (missing/empty to or label) are stripped so they never reach
  // NavLink — where a missing `to` throws and a missing `label` crashes toLowerCase().
  const _validNav = (arr) => (arr || []).filter((it) => it && typeof it.to === "string" && it.to && typeof it.label === "string" && it.label);
  const allItems = _validNav(site?.nav_items).length ? _validNav(site.nav_items) : DEFAULT_NAV;
  const desktopItems = allItems.filter((it) => it.visible !== false && it.device !== "mobile");
  // Use independent mobile_nav_items if defined; otherwise filter desktop nav_items.
  // Safety net: fall back through desktop-derived items → DEFAULT_NAV so the drawer
  // can never render with zero links regardless of what was saved in Header Builder.
  const mobileFallbackItems = allItems.filter((it) => it.visible !== false && it.device !== "desktop");
  const mobileItemsRaw = _validNav(site?.mobile_nav_items).length
    ? _validNav(site.mobile_nav_items).filter((it) => it.visible !== false)
    : mobileFallbackItems;
  const mobileItems = mobileItemsRaw.length
    ? mobileItemsRaw
    : (mobileFallbackItems.length ? mobileFallbackItems : DEFAULT_NAV);
  const ctaText = site?.cta_text || "Book a Training";
  const ctaLink = site?.cta_link || "/book";

  const navTextColor = site?.nav_text_color || "#4B5563";
  const navHoverColor = site?.nav_hover_color || "#0F172A";
  const navActiveColor = site?.nav_active_color || "#0F172A";
  const mobileNavHover  = site?.mobile_nav_hover_color  || "#4F46E5";
  const mobileNavActive = site?.mobile_nav_active_color || "#4F46E5";
  const navVars = { "--nav-text": navTextColor, "--nav-hover": navHoverColor, "--nav-active": navActiveColor };
  const navStyle = {
    fontSize: site?.nav_font_size ? `${site.nav_font_size}px` : undefined,
    fontWeight: site?.nav_font_weight ?? 500,
    letterSpacing: site?.nav_letter_spacing != null ? `${site.nav_letter_spacing}em` : undefined,
    fontFamily: site?.nav_font_family ? fontCss(site.nav_font_family) : undefined,
  };

  // Mobile breakpoint from CMS (default 768px = md) — bpValue defined above for matchMedia
  const bp = bpValue;
  const bpPx = `${bp}px`;
  const navItemPaddingStyle = {
    paddingTop: site?.nav_item_padding_top ?? 8,
    paddingRight: site?.nav_item_padding_right ?? 12,
    paddingBottom: site?.nav_item_padding_bottom ?? 8,
    paddingLeft: site?.nav_item_padding_left ?? 12,
  };
  // Resolved mobile values (fall back to desktop equivalents)
  // Mobile logo — NEVER falls back to desktop logo_url
  const mobileLogoUrl = site?.mobile_logo_url || "";
  const mobileLogoW   = site?.mobile_logo_width  ?? 32;
  const mobileLogoH   = site?.mobile_logo_height ?? 32;
  const mobileCTAText = site?.mobile_cta_text || ctaText;
  const mobileSigninText = site?.mobile_signin_text || site?.signin_btn_text || "Sign in";
  const mobileNavStyle = {
    fontFamily: site?.mobile_nav_font_family ? `'${site.mobile_nav_font_family}', sans-serif` : undefined,
    fontSize: site?.mobile_nav_font_size ? `${site.mobile_nav_font_size}px` : undefined,
    fontWeight: site?.mobile_nav_font_weight ?? undefined,
    fontStyle: site?.mobile_nav_font_style && site.mobile_nav_font_style !== "normal" ? site.mobile_nav_font_style : undefined,
    textDecoration: site?.mobile_nav_text_decoration && site.mobile_nav_text_decoration !== "none" ? site.mobile_nav_text_decoration : undefined,
    color: site?.mobile_nav_color || undefined,
  };
  const mobileNavItemStyle = {
    paddingTop: site?.mobile_nav_item_padding_top ?? 12,
    paddingRight: site?.mobile_nav_item_padding_right ?? 12,
    paddingBottom: site?.mobile_nav_item_padding_bottom ?? 12,
    paddingLeft: site?.mobile_nav_item_padding_left ?? 12,
    borderRadius: site?.mobile_nav_item_border_radius ?? 12,
  };
  const mobileNameStyle = {
    fontFamily: site?.mobile_name_font_family ? `'${site.mobile_name_font_family}', sans-serif` : undefined,
    fontSize: site?.mobile_name_font_size ? `${site.mobile_name_font_size}px` : undefined,
    fontWeight: site?.mobile_name_font_weight ?? 600,
    fontStyle: site?.mobile_name_font_style || undefined,
    letterSpacing: site?.mobile_name_letter_spacing ? `${site.mobile_name_letter_spacing}em` : undefined,
    lineHeight: site?.mobile_name_line_height ?? 1.2,
    color: site?.mobile_name_color || "#0F172A",
    textAlign: site?.mobile_name_align || "left",
    textTransform: site?.mobile_name_text_transform && site?.mobile_name_text_transform !== "none" ? site.mobile_name_text_transform : undefined,
    textDecoration: site?.mobile_name_text_decoration && site.mobile_name_text_decoration !== "none" ? site.mobile_name_text_decoration : undefined,
    textShadow: site?.mobile_name_text_shadow || undefined,
    marginTop: site?.mobile_name_margin_top || undefined,
    marginRight: site?.mobile_name_margin_right || undefined,
    marginBottom: site?.mobile_name_margin_bottom || undefined,
    marginLeft: site?.mobile_name_margin_left ?? 6,
  };
  // Build transform combining rotation + scale + offset
  const _mobileLogoScale = site?.mobile_logo_scale ?? 1;
  const _mobileLogoRot   = site?.mobile_logo_rotation ?? 0;
  const _mobileLogoOffX  = site?.mobile_logo_offset_x ?? 0;
  const _mobileLogoOffY  = site?.mobile_logo_offset_y ?? 0;
  const _mobileLogoTransform = [
    _mobileLogoRot  ? `rotate(${_mobileLogoRot}deg)`       : "",
    _mobileLogoScale !== 1 ? `scale(${_mobileLogoScale})`  : "",
    (_mobileLogoOffX || _mobileLogoOffY) ? `translate(${_mobileLogoOffX}px, ${_mobileLogoOffY}px)` : "",
  ].filter(Boolean).join(" ") || undefined;

  const mobileLogoStyle = {
    width: mobileLogoW,
    height: mobileLogoH,
    minWidth: site?.mobile_logo_min_width  ? `${site.mobile_logo_min_width}px`  : undefined,
    minHeight: site?.mobile_logo_min_height ? `${site.mobile_logo_min_height}px` : undefined,
    maxWidth: site?.mobile_logo_max_width  ? `${site.mobile_logo_max_width}px`  : undefined,
    maxHeight: site?.mobile_logo_max_height ? `${site.mobile_logo_max_height}px` : undefined,
    objectFit: site?.mobile_logo_object_fit || "contain",
    borderRadius: site?.mobile_logo_border_radius ?? 8,
    background: site?.mobile_logo_bg || undefined,
    opacity: site?.mobile_logo_opacity ?? 1,
    transform: _mobileLogoTransform,
    boxShadow: site?.mobile_logo_shadow || undefined,
    zIndex: site?.mobile_logo_zindex || undefined,
    marginTop: site?.mobile_logo_margin_top || undefined,
    marginRight: site?.mobile_logo_margin_right ?? 8,
    marginBottom: site?.mobile_logo_margin_bottom || undefined,
    marginLeft: site?.mobile_logo_margin_left || undefined,
    paddingTop: site?.mobile_logo_padding_top || undefined,
    paddingRight: site?.mobile_logo_padding_right || undefined,
    paddingBottom: site?.mobile_logo_padding_bottom || undefined,
    paddingLeft: site?.mobile_logo_padding_left || undefined,
  };
  // Alignment: flex justify on the logo wrapper
  const mobileLogoAlign = site?.mobile_logo_align || "left";
  const mobileLogoJustify = mobileLogoAlign === "center" ? "center" : mobileLogoAlign === "right" ? "flex-end" : "flex-start";
  const mobileHeaderStyle = {
    minHeight: site?.mobile_header_height ?? 56,
    maxWidth: site?.mobile_header_max_width || undefined,
    width: site?.mobile_header_width || undefined,
    background: site?.mobile_header_transparent ? "transparent" : (site?.mobile_header_gradient || site?.mobile_header_bg || "#FFFFFF"),
    backdropFilter: site?.mobile_header_blur ? "blur(12px) saturate(150%)" : undefined,
    WebkitBackdropFilter: site?.mobile_header_blur ? "blur(12px) saturate(150%)" : undefined,
    boxShadow: site?.mobile_header_shadow !== false ? "0 1px 3px rgba(0,0,0,0.08)" : undefined,
    borderBottom: site?.mobile_header_border ? `1px solid ${site?.mobile_header_border_color || "#E2E8F0"}` : undefined,
    borderRadius: site?.mobile_header_border_radius || undefined,
    paddingTop: site?.mobile_header_padding_top ?? 10,
    paddingBottom: site?.mobile_header_padding_bottom ?? 10,
    paddingLeft: site?.mobile_header_padding_left ?? 16,
    paddingRight: site?.mobile_header_padding_right ?? 16,
    opacity: site?.mobile_header_opacity ?? 1,
    zIndex: site?.mobile_header_zindex ?? undefined,
    position: site?.mobile_header_fixed ? "fixed" : undefined,
    top: site?.mobile_header_fixed ? 0 : undefined,
    left: site?.mobile_header_fixed ? 0 : undefined,
    right: site?.mobile_header_fixed ? 0 : undefined,
  };
  const hamburgerBtnStyle = {
    background: site?.mobile_hamburger_bg || "transparent",
    borderRadius: site?.mobile_hamburger_border_radius ?? 8,
    padding: site?.mobile_hamburger_padding ?? 8,
    color: site?.mobile_hamburger_color || "#374151",
    marginTop: site?.mobile_hamburger_margin_top || undefined,
    marginRight: site?.mobile_hamburger_margin_right || undefined,
    marginBottom: site?.mobile_hamburger_margin_bottom || undefined,
    marginLeft: site?.mobile_hamburger_margin_left || undefined,
  };
  const drawerStyle = {
    background: site?.mobile_menu_bg || "#FFFFFF",
    maxWidth: (site?.mobile_menu_position === "left" || site?.mobile_menu_position === "right")
      ? `${site?.mobile_menu_width ?? 280}px` : undefined,
    marginLeft: site?.mobile_menu_position === "right" ? "auto" : undefined,
    borderRadius: site?.mobile_menu_border_radius || undefined,
    boxShadow: site?.mobile_menu_shadow !== false ? "0 8px 32px rgba(0,0,0,0.12)" : undefined,
  };
  const overlayStyle = {
    backgroundColor: site?.mobile_overlay_color || "#000000",
    opacity: site?.mobile_overlay_opacity ?? 0.3,
    backdropFilter: "blur(4px)",
    WebkitBackdropFilter: "blur(4px)",
  };

  return (
    <nav className="glass sticky top-0 z-40 border-b border-gray-100" style={{ ...navVars, "--mobile-nav-hover": mobileNavHover, "--mobile-nav-active": mobileNavActive, borderRadius: site?.header_border_radius || undefined }}>
      <style>{`
        .mnav-link { color: var(--nav-text); }
        .mnav-link:hover { color: var(--nav-hover); }
        .mnav-link.active { color: var(--nav-active); font-weight: 600; }
        .mnav-links-scroll { scrollbar-width: none; }
        .mnav-links-scroll::-webkit-scrollbar { display: none; }
        @media (min-width: ${bpPx}) { .mnav-desktop { display: flex !important; } .mnav-mobile-only { display: none !important; } }
        @media (max-width: calc(${bpPx} - 1px)) { .mnav-desktop { display: none !important; } .mnav-mobile-only { display: flex !important; } }
        @media (max-width: calc(${bpPx} - 1px)) { .mnav-bar { min-height: ${site?.mobile_header_height ?? 56}px !important; } }
        @media(max-width:374px){.mnav-cta-header{display:none!important;}}
        @media(max-width:calc(${bpPx} - 1px)){.mnav-bar{overflow:hidden;}}
        @keyframes fadeInMenu { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: translateY(0); } }
        .mnav-mobile-link:hover { color: var(--mobile-nav-hover, #4F46E5) !important; background: rgba(0,0,0,0.03); }
        .mnav-mobile-link.active { color: var(--mobile-nav-active, #4F46E5) !important; }
        ${(() => {
          // Tablet range: starts exactly where the CMS-configurable mobile
          // breakpoint ends (matching the desktop/mobile switch above), and
          // spans the same 255px width the old hardcoded 768–1023 range used —
          // so these overrides never conflict with a customized breakpoint.
          const t = site;
          if (!t) return "";
          const tabletUpper = bp + 255;
          let css = `@media(min-width:${bpPx}) and (max-width:${tabletUpper}px){`;
          if (t.tablet_logo_width != null)          css += `.mnav-logo img,.mnav-logo-box{width:${t.tablet_logo_width}px!important;}`;
          if (t.tablet_logo_height != null)         css += `.mnav-logo img,.mnav-logo-box{height:${t.tablet_logo_height}px!important;}`;
          if (t.tablet_logo_visible === false)      css += `.mnav-logo{display:none!important;}`;
          if (t.tablet_company_name_visible === false) css += `.mnav-company-name{display:none!important;}`;
          if (t.tablet_company_name_font_size != null) css += `.mnav-company-name{font-size:${t.tablet_company_name_font_size}px!important;}`;
          if (t.tablet_company_name_max_width != null) css += `.mnav-company-name{max-width:${t.tablet_company_name_max_width}px!important;}`;
          if (t.tablet_nav_visible === false)       css += `.mnav-links{display:none!important;}`;
          if (t.tablet_nav_font_size != null)       css += `.mnav-link{font-size:${t.tablet_nav_font_size}px!important;}`;
          if (t.tablet_nav_gap != null)             css += `.mnav-links{gap:${t.tablet_nav_gap}px!important;}`;
          if (t.tablet_cta_btn_visible === false)   css += `.mnav-cta{display:none!important;}`;
          if (t.tablet_signin_btn_visible === false) css += `.mnav-signin{display:none!important;}`;
          if (t.tablet_cta_btn_font_size != null)   css += `.mnav-cta{font-size:${t.tablet_cta_btn_font_size}px!important;}`;
          if (t.tablet_signin_btn_font_size != null) css += `.mnav-signin{font-size:${t.tablet_signin_btn_font_size}px!important;}`;
          if (t.tablet_cta_btn_padding_x != null)   css += `.mnav-cta{padding-left:${t.tablet_cta_btn_padding_x}px!important;padding-right:${t.tablet_cta_btn_padding_x}px!important;}`;
          if (t.tablet_cta_btn_padding_y != null)   css += `.mnav-cta{padding-top:${t.tablet_cta_btn_padding_y}px!important;padding-bottom:${t.tablet_cta_btn_padding_y}px!important;}`;
          if (t.tablet_signin_btn_padding_x != null) css += `.mnav-signin{padding-left:${t.tablet_signin_btn_padding_x}px!important;padding-right:${t.tablet_signin_btn_padding_x}px!important;}`;
          if (t.tablet_header_height != null)       css += `.mnav-bar{min-height:${t.tablet_header_height}px!important;}`;
          if (t.tablet_header_padding_x != null)    css += `.mnav-bar{padding-inline:${t.tablet_header_padding_x}px!important;}`;
          if (t.tablet_header_padding_top != null)    css += `.mnav-bar{padding-top:${t.tablet_header_padding_top}px!important;}`;
          if (t.tablet_header_padding_bottom != null) css += `.mnav-bar{padding-bottom:${t.tablet_header_padding_bottom}px!important;}`;
          if (t.tablet_header_margin_top != null)    css += `.mnav-bar{margin-top:${t.tablet_header_margin_top}px!important;}`;
          if (t.tablet_header_margin_right != null)  css += `.mnav-bar{margin-right:${t.tablet_header_margin_right}px!important;}`;
          if (t.tablet_header_margin_bottom != null) css += `.mnav-bar{margin-bottom:${t.tablet_header_margin_bottom}px!important;}`;
          if (t.tablet_header_margin_left != null)   css += `.mnav-bar{margin-left:${t.tablet_header_margin_left}px!important;}`;
          if (t.tablet_logo_margin_top != null)      css += `.mnav-logo{margin-top:${t.tablet_logo_margin_top}px!important;}`;
          if (t.tablet_logo_margin_right != null)    css += `.mnav-logo{margin-right:${t.tablet_logo_margin_right}px!important;}`;
          if (t.tablet_logo_margin_bottom != null)   css += `.mnav-logo{margin-bottom:${t.tablet_logo_margin_bottom}px!important;}`;
          if (t.tablet_logo_margin_left != null)     css += `.mnav-logo{margin-left:${t.tablet_logo_margin_left}px!important;}`;
          if (t.tablet_logo_padding_top != null)     css += `.mnav-logo{padding-top:${t.tablet_logo_padding_top}px!important;}`;
          if (t.tablet_logo_padding_right != null)   css += `.mnav-logo{padding-right:${t.tablet_logo_padding_right}px!important;}`;
          if (t.tablet_logo_padding_bottom != null)  css += `.mnav-logo{padding-bottom:${t.tablet_logo_padding_bottom}px!important;}`;
          if (t.tablet_logo_padding_left != null)    css += `.mnav-logo{padding-left:${t.tablet_logo_padding_left}px!important;}`;
          if (t.tablet_company_name_margin_top != null)    css += `.mnav-company-name{margin-top:${t.tablet_company_name_margin_top}px!important;}`;
          if (t.tablet_company_name_margin_right != null)  css += `.mnav-company-name{margin-right:${t.tablet_company_name_margin_right}px!important;}`;
          if (t.tablet_company_name_margin_bottom != null) css += `.mnav-company-name{margin-bottom:${t.tablet_company_name_margin_bottom}px!important;}`;
          if (t.tablet_company_name_margin_left != null)   css += `.mnav-company-name{margin-left:${t.tablet_company_name_margin_left}px!important;}`;
          if (t.tablet_nav_margin_top != null)       css += `.mnav-links{margin-top:${t.tablet_nav_margin_top}px!important;}`;
          if (t.tablet_nav_margin_right != null)     css += `.mnav-links{margin-right:${t.tablet_nav_margin_right}px!important;}`;
          if (t.tablet_nav_margin_bottom != null)    css += `.mnav-links{margin-bottom:${t.tablet_nav_margin_bottom}px!important;}`;
          if (t.tablet_nav_margin_left != null)      css += `.mnav-links{margin-left:${t.tablet_nav_margin_left}px!important;}`;
          if (t.tablet_cta_btn_margin_top != null)      css += `.mnav-cta{margin-top:${t.tablet_cta_btn_margin_top}px!important;}`;
          if (t.tablet_cta_btn_margin_right != null)    css += `.mnav-cta{margin-right:${t.tablet_cta_btn_margin_right}px!important;}`;
          if (t.tablet_cta_btn_margin_bottom != null)   css += `.mnav-cta{margin-bottom:${t.tablet_cta_btn_margin_bottom}px!important;}`;
          if (t.tablet_cta_btn_margin_left != null)     css += `.mnav-cta{margin-left:${t.tablet_cta_btn_margin_left}px!important;}`;
          if (t.tablet_signin_btn_padding_y != null) css += `.mnav-signin{padding-top:${t.tablet_signin_btn_padding_y}px!important;padding-bottom:${t.tablet_signin_btn_padding_y}px!important;}`;
          if (t.tablet_signin_btn_margin_top != null)    css += `.mnav-signin{margin-top:${t.tablet_signin_btn_margin_top}px!important;}`;
          if (t.tablet_signin_btn_margin_right != null)  css += `.mnav-signin{margin-right:${t.tablet_signin_btn_margin_right}px!important;}`;
          if (t.tablet_signin_btn_margin_bottom != null) css += `.mnav-signin{margin-bottom:${t.tablet_signin_btn_margin_bottom}px!important;}`;
          if (t.tablet_signin_btn_margin_left != null)   css += `.mnav-signin{margin-left:${t.tablet_signin_btn_margin_left}px!important;}`;
          css += "}";
          return css === `@media(min-width:${bpPx}) and (max-width:${tabletUpper}px){}` ? "" : css;
        })()}
      `}</style>

      {/* ── Main bar ─────────────────────────────────────────────────── */}
      <div
        className="mnav-bar mx-auto flex items-center gap-2 xl:gap-4"
        style={{
          maxWidth: site?.header_max_width ?? 1280,
          paddingTop: site?.nav_padding_top ?? 10,
          paddingBottom: site?.nav_padding_bottom ?? 10,
          paddingLeft: site?.nav_padding_left ?? undefined,
          paddingRight: site?.nav_padding_right ?? undefined,
          minHeight: site?.header_height ?? 64,
          paddingInline: "max(16px, min(32px, 4vw))",
        }}
      >
                {/* Desktop logo — only shown above breakpoint via CSS */}
        <div
          className="mnav-logo shrink-0 min-w-0 max-w-[200px] sm:max-w-[240px] mnav-desktop"
          style={{
            marginTop: site?.logo_margin_top || undefined,
            marginRight: site?.logo_margin_right || undefined,
            marginBottom: site?.logo_margin_bottom || undefined,
            marginLeft: site?.logo_margin_left || undefined,
            paddingTop: site?.logo_padding_top || undefined,
            paddingRight: site?.logo_padding_right || undefined,
            paddingBottom: site?.logo_padding_bottom || undefined,
            paddingLeft: site?.logo_padding_left || undefined,
          }}
        >
          <Logo />
        </div>

        {/* Mobile logo — independent from desktop, uses mobile_* CMS fields only */}
        <Link
          to="/"
          onClick={closeMobile}
          className="mnav-mobile-only hidden items-center min-w-0 flex-1 overflow-hidden"
          style={{ justifyContent: mobileLogoJustify, textDecoration: "none" }}
        >
          {site?.mobile_logo_visible !== false && mobileLogoUrl && (
            <img
              src={mobileLogoUrl.startsWith("http") ? mobileLogoUrl : `${process.env.REACT_APP_BACKEND_URL}${mobileLogoUrl}`}
              alt={resolveHeaderCompanyName(site, site?.mobile_company_name) || "Logo"}
              data-testid="mobile-header-logo"
              style={mobileLogoStyle}
            />
          )}
          {site?.mobile_company_name_visible !== false && resolveHeaderCompanyName(site, site?.mobile_company_name) && (
            <div style={{ display: "flex", flexDirection: "column", minWidth: 0, overflow: "hidden" }}>
              <span style={{ ...mobileNameStyle, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "min(160px, 40vw)" }}>
                {resolveHeaderCompanyName(site, site?.mobile_company_name)}
              </span>
              {site?.mobile_tagline_visible && site?.mobile_tagline && (
                <span style={{ fontSize: "10px", color: "#94A3B8", lineHeight: 1.3 }}>{site.mobile_tagline}</span>
              )}
            </div>
          )}
        </Link>

        {/* Desktop nav links — hidden below breakpoint via CSS */}
        <div
          className="mnav-links mnav-desktop hidden items-center flex-1 min-w-0 overflow-x-auto overflow-y-hidden mnav-links-scroll"
          style={{
            gap: site?.nav_gap != null ? site.nav_gap : 2,
            marginTop: site?.nav_margin_top || undefined,
            marginRight: site?.nav_margin_right || undefined,
            marginBottom: site?.nav_margin_bottom || undefined,
            marginLeft: site?.nav_margin_left || undefined,
          }}
        >
          {desktopItems.map((it, idx) => (
            <NavLink
              key={it.id || it.to || idx}
              to={it.to || "/"}
              data-testid={`nav-${(it.label || "link").toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `mnav-link whitespace-nowrap transition-colors shrink-0 text-sm${isActive ? " active" : ""}`
              }
              style={{ ...navStyle, ...navItemPaddingStyle, borderRadius: `${site?.nav_item_border_radius ?? 8}px` }}
            >
              {it.label}
            </NavLink>
          ))}
        </div>

        {/* Desktop right actions — hidden below breakpoint via CSS */}
        <div className="mnav-desktop hidden items-center gap-1.5 shrink-0">
          {!site?.hide_login && site?.signin_btn_visible !== false && (
            <Link
              to="/login"
              data-testid="nav-login"
              className="mnav-signin inline-flex items-center whitespace-nowrap transition-colors"
              style={{
                color: site?.signin_btn_color || "#374151",
                fontSize: site?.signin_btn_font_size ? `${site.signin_btn_font_size}px` : "14px",
                fontWeight: site?.signin_btn_font_weight ?? 500,
                background: site?.signin_btn_bg || "transparent",
                borderRadius: `${site?.signin_btn_border_radius ?? 8}px`,
                paddingLeft: `${site?.signin_btn_padding_x ?? 12}px`,
                paddingRight: `${site?.signin_btn_padding_x ?? 12}px`,
                paddingTop: `${site?.signin_btn_padding_y ?? 8}px`,
                paddingBottom: `${site?.signin_btn_padding_y ?? 8}px`,
                border: site?.signin_btn_border && site?.signin_btn_border !== "none"
                  ? `1px solid ${site?.signin_btn_border_color || "#E2E8F0"}` : undefined,
                marginTop: site?.signin_btn_margin_top || undefined,
                marginRight: site?.signin_btn_margin_right || undefined,
                marginBottom: site?.signin_btn_margin_bottom || undefined,
                marginLeft: site?.signin_btn_margin_left || undefined,
              }}
            >
              {site?.signin_btn_text || "Sign in"}
            </Link>
          )}
          {site?.cta_btn_visible !== false && (
            <Link
              to={ctaLink}
              data-testid="nav-book-cta"
              className="mnav-cta inline-flex items-center gap-1.5 transition-all group whitespace-nowrap shrink-0 hover:opacity-90"
              style={{
                background: site?.cta_btn_bg || "#0F172A",
                color: site?.cta_btn_color || "#FFFFFF",
                fontSize: site?.cta_btn_font_size ? `${site.cta_btn_font_size}px` : "14px",
                fontWeight: site?.cta_btn_font_weight ?? 500,
                borderRadius: `${site?.cta_btn_border_radius ?? 8}px`,
                paddingLeft: `${site?.cta_btn_padding_x ?? 16}px`,
                paddingRight: `${site?.cta_btn_padding_x ?? 16}px`,
                paddingTop: `${site?.cta_btn_padding_y ?? 8}px`,
                paddingBottom: `${site?.cta_btn_padding_y ?? 8}px`,
                marginTop: site?.cta_btn_margin_top || undefined,
                marginRight: site?.cta_btn_margin_right || undefined,
                marginBottom: site?.cta_btn_margin_bottom || undefined,
                marginLeft: site?.cta_btn_margin_left || undefined,
              }}
            >
              {ctaText}
              <ArrowUpRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </Link>
          )}
        </div>

        {/* Mobile right: CTA + hamburger — always shrink-0 so it never gets pushed off */}
        <div className="mnav-mobile-only hidden items-center gap-1.5 shrink-0 ml-auto">
          {site?.mobile_show_cta !== false && (
            <Link
              to={ctaLink}
              className="mnav-cta-header inline-flex items-center gap-1 font-semibold transition-all whitespace-nowrap max-w-[120px] overflow-hidden text-ellipsis"
              style={{
                background: site?.mobile_cta_bg || site?.cta_btn_bg || "#0F172A",
                color: site?.mobile_cta_color || "#FFFFFF",
                fontSize: site?.mobile_cta_font_size ? `${site.mobile_cta_font_size}px` : "13px",
                fontWeight: site?.mobile_cta_font_weight ?? 600,
                borderRadius: `${site?.mobile_cta_border_radius ?? 8}px`,
                paddingLeft: `${site?.mobile_cta_padding_x ?? 14}px`,
                paddingRight: `${site?.mobile_cta_padding_x ?? 14}px`,
                paddingTop: `${site?.mobile_cta_padding_y ?? 8}px`,
                paddingBottom: `${site?.mobile_cta_padding_y ?? 8}px`,
                marginTop: site?.mobile_cta_margin_top || undefined,
                marginRight: site?.mobile_cta_margin_right || undefined,
                marginBottom: site?.mobile_cta_margin_bottom || undefined,
                marginLeft: site?.mobile_cta_margin_left || undefined,
              }}
            >
              {mobileCTAText}
            </Link>
          )}
          <button
            onClick={() => setMobileOpen((v) => !v)}
            className="grid place-items-center hover:bg-gray-100 transition-colors"
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileOpen}
            aria-controls="nav-mobile-menu"
            data-testid="nav-mobile-toggle"
            style={hamburgerBtnStyle}
          >
            {mobileOpen
              ? <X style={{ width: site?.mobile_hamburger_size ?? 20, height: site?.mobile_hamburger_size ?? 20, color: site?.mobile_hamburger_color || "#374151" }} />
              : <div>{[0,1,2].map((i) => (<div key={i} style={{ width: site?.mobile_hamburger_size ?? 20, height: site?.mobile_hamburger_thickness ?? 2, background: site?.mobile_hamburger_color || "#374151", borderRadius: 2, marginBottom: i < 2 ? (site?.mobile_hamburger_thickness ?? 2) + 2 : 0 }} />))}</div>
            }
          </button>
        </div>
      </div>

      {/* ── Mobile drawer — portalled to document.body so it escapes the
           backdrop-filter / sticky stacking context on <nav> which would
           otherwise make position:fixed relative to the nav instead of
           the viewport (CSS spec: backdrop-filter creates a containing
           block for fixed-position descendants). ─────────────────────── */}
      {drawerMounted && createPortal(
        <>
          {/* Backdrop overlay — fades in/out with the drawer */}
          {site?.mobile_menu_overlay !== false && (
            <div
              className="fixed inset-0 z-[9998]"
              style={{
                ...overlayStyle,
                opacity: drawerVisible ? (overlayStyle.opacity ?? 1) : 0,
                transition: "opacity 280ms ease",
                pointerEvents: drawerVisible ? "auto" : "none",
              }}
              onClick={closeMobile}
              aria-hidden="true"
            />
          )}
          {/* Slide panel — fixed, full-height, scrollable internally */}
          <div
            id="nav-mobile-menu"
            role="dialog"
            aria-modal="true"
            aria-label="Navigation menu"
            data-testid="nav-mobile-menu"
            className="fixed top-0 bottom-0 z-[9999] flex flex-col overflow-hidden"
            onTouchStart={onDrawerTouchStart}
            onTouchEnd={(e) => onDrawerTouchEnd(e, site?.mobile_menu_position === "right" ? "right" : "left")}
            style={{
              ...drawerStyle,
              "--mobile-nav-hover": mobileNavHover,
              "--mobile-nav-active": mobileNavActive,
              left: site?.mobile_menu_position === "right" ? "auto" : 0,
              right: site?.mobile_menu_position === "right" ? 0 : "auto",
              width: `min(${site?.mobile_menu_width ?? 280}px, 100vw)`,
              maxWidth: "100vw",
              transform: drawerVisible
                ? "translate3d(0, 0, 0)"
                : `translate3d(${site?.mobile_menu_position === "right" ? "100%" : "-100%"}, 0, 0)`,
              transition: `transform ${site?.mobile_menu_animation_speed ?? 220}ms cubic-bezier(0.16, 1, 0.3, 1)`,
              willChange: "transform",
              paddingBottom: "max(env(safe-area-inset-bottom), 0px)",
            }}
          >
            {/* Drawer header row */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 shrink-0"
              style={{ minHeight: site?.mobile_header_height ?? 56, paddingTop: "max(env(safe-area-inset-top), 12px)" }}>
              {/* Logo in drawer */}
              {mobileLogoUrl && site?.mobile_logo_visible !== false && (
                <img
                  src={mobileLogoUrl.startsWith("http") ? mobileLogoUrl : `${process.env.REACT_APP_BACKEND_URL}${mobileLogoUrl}`}
                  alt={resolveHeaderCompanyName(site, site?.mobile_company_name) || "Logo"}
                  style={{ ...mobileLogoStyle, marginRight: 8 }}
                />
              )}
              {site?.mobile_company_name_visible !== false && resolveHeaderCompanyName(site, site?.mobile_company_name) && (
                <span className="flex-1 font-semibold text-sm truncate" style={mobileNameStyle}>
                  {resolveHeaderCompanyName(site, site?.mobile_company_name)}
                </span>
              )}
              <button
                onClick={closeMobile}
                className="ml-auto w-9 h-9 grid place-items-center rounded-lg hover:bg-gray-100 transition-colors shrink-0"
                aria-label="Close menu"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* Scrollable nav links */}
            <div
              className="flex-1 overflow-y-auto overscroll-contain"
              style={{ padding: "8px 12px", gap: site?.mobile_nav_gap != null ? `${site.mobile_nav_gap}px` : "2px", display: "flex", flexDirection: "column", position: "relative", zIndex: 2 }}
            >
              {mobileItems.map((it, idx) => (
                <NavLink
                  key={it.id || it.to || idx}
                  to={it.to || "/"}
                  onClick={closeMobile}
                  data-testid={`nav-mobile-${(it.label || "link").toLowerCase().replace(/\s+/g, "-")}`}
                  className={({ isActive }) =>
                    `mnav-link mnav-mobile-link min-h-[44px] flex items-center transition-colors${isActive ? " active" : ""}`
                  }
                  style={{ ...mobileNavStyle, ...mobileNavItemStyle, display: "flex", width: "100%", position: "relative", zIndex: 1 }}
                >
                  {it.label}
                </NavLink>
              ))}

              {/* Sign in + CTA at bottom of drawer */}
              <div className="mt-2 pt-2 border-t border-gray-100 flex flex-col gap-2 pb-4">
                {site?.mobile_show_signin !== false && !site?.hide_login && (
                  <Link
                    to="/login"
                    onClick={closeMobile}
                    className="min-h-[44px] flex items-center transition-colors hover:bg-gray-50"
                    style={{
                      color: site?.mobile_signin_color || "#374151",
                      fontSize: site?.mobile_signin_font_size ? `${site.mobile_signin_font_size}px` : "14px",
                      fontWeight: site?.mobile_signin_font_weight ?? 500,
                      background: site?.mobile_signin_bg && site?.mobile_signin_bg !== "transparent" ? site.mobile_signin_bg : undefined,
                      border: site?.mobile_signin_border && site?.mobile_signin_border !== "none" ? `1px solid ${site?.mobile_signin_border_color || "#E2E8F0"}` : undefined,
                      borderRadius: `${site?.mobile_signin_border_radius ?? 8}px`,
                      paddingLeft: `${site?.mobile_signin_padding_x ?? 12}px`,
                      paddingRight: `${site?.mobile_signin_padding_x ?? 12}px`,
                      paddingTop: `${site?.mobile_signin_padding_y ?? 8}px`,
                      paddingBottom: `${site?.mobile_signin_padding_y ?? 8}px`,
                      width: site?.mobile_signin_width || undefined,
                      justifyContent: site?.mobile_signin_align === "center" ? "center" : site?.mobile_signin_align === "right" ? "flex-end" : undefined,
                    }}
                  >
                    {mobileSigninText}
                  </Link>
                )}
                {site?.mobile_show_cta !== false && (
                  <Link
                    to={ctaLink}
                    onClick={closeMobile}
                    className="inline-flex items-center gap-1.5 font-semibold transition-colors"
                    style={{
                      background: site?.mobile_cta_bg || site?.cta_btn_bg || "#0F172A",
                      color: site?.mobile_cta_color || "#FFFFFF",
                      fontSize: site?.mobile_cta_font_size ? `${site.mobile_cta_font_size}px` : "14px",
                      fontWeight: site?.mobile_cta_font_weight ?? 600,
                      borderRadius: `${site?.mobile_cta_border_radius ?? 12}px`,
                      paddingLeft: `${site?.mobile_cta_padding_x ?? 16}px`,
                      paddingRight: `${site?.mobile_cta_padding_x ?? 16}px`,
                      paddingTop: `${site?.mobile_cta_padding_y ?? 12}px`,
                      paddingBottom: `${site?.mobile_cta_padding_y ?? 12}px`,
                      border: site?.mobile_cta_border && site?.mobile_cta_border !== "none" ? `1px solid ${site?.mobile_cta_border_color || "#E2E8F0"}` : undefined,
                      width: site?.mobile_cta_width || undefined,
                      justifyContent: site?.mobile_cta_align === "left" ? "flex-start" : site?.mobile_cta_align === "right" ? "flex-end" : "center",
                    }}
                  >
                    {mobileCTAText} <ArrowUpRight className="w-3.5 h-3.5" />
                  </Link>
                )}
              </div>
            </div>
          </div>
        </>,
        document.body
      )}
    </nav>
  );
};

export const Footer = ({ previewSite = null }) => {
  const ctx = useCms();
  const site = previewSite || ctx.site;
  const name = site?.company_name || "";
  const email = site?.contact_email || "hello@edulift.com";
  const phone = site?.contact_phone || "+91 98450 12345";
  const whatsapp = site?.whatsapp || "";
  const address = site?.address || "Bengaluru · Mumbai · Delhi";
  const description = site?.footer_description || "Empowering schools through innovative teacher training, professional development, and QR-verified certification programs.";
  const social = site?.social || {};

  const bg = site?.footer_bg_color || "#F8FAFC";
  const textColor = site?.footer_text_color || "#64748B";
  const linkColor = site?.footer_link_color || "#64748B";
  const hoverColor = site?.footer_link_hover_color || "#3B68E7";
  const dividerColor = site?.footer_divider_color || "#E2E8F0";
  const sectionSpacing = site?.footer_section_spacing ?? 80;
  const columnGap = site?.footer_column_gap ?? 40;

  // Footer logo — independent from the header logo; falls back to it when unset.
  // Sizing: footer_logo_width/height are the new granular fields; footer_logo_size
  // (legacy, single value used for both width and height) is kept as a fallback so
  // footers saved before these fields existed don't change appearance.
  const footerLogoUrl = site?.footer_logo_url || site?.logo_url || "";
  const footerLogoWidth = site?.footer_logo_width ?? site?.footer_logo_size ?? 24;
  const footerLogoHeight = site?.footer_logo_height ?? site?.footer_logo_size ?? 24;
  const footerLogoAlign = site?.footer_logo_align === "center" ? "center" : site?.footer_logo_align === "right" ? "flex-end" : "flex-start";

  // Only routes that actually exist in this app — omitted rather than pointing at a 404,
  // per "never break navigation." Falls back to these defaults if the CMS doc predates
  // the footer_columns field.
  const columns = (site?.footer_columns && site.footer_columns.length ? site.footer_columns : [
    { title: "Company", visible: true, links: [{ label: "About", url: "/about", visible: true }, { label: "Programs", url: "/programs", visible: true }, { label: "Contact", url: "/contact", visible: true }] },
    { title: "Quick Links", visible: true, links: [{ label: "Home", url: "/", visible: true }, { label: "About", url: "/about", visible: true }, { label: "Programs", url: "/programs", visible: true }, { label: "Gallery", url: "/gallery", visible: true }, { label: "Skilled Teachers", url: "/experts", visible: true }, { label: "Certificate Verification", url: "/verify", visible: true }, { label: "FAQ", url: "/faq", visible: true }, { label: "Contact", url: "/contact", visible: true }] },
  ]).filter((c) => c.visible !== false);

  const socialIcons = [
    ["linkedin", Linkedin], ["twitter", Twitter], ["instagram", Instagram], ["youtube", Youtube], ["facebook", Facebook],
  ];

  const linkStyle = { color: linkColor };
  const linkCls = "text-sm hover:underline underline-offset-2 transition-colors duration-200";

  return (
    <footer style={{ backgroundColor: bg }} data-testid="site-footer">
      {(site?.footer_cta_heading || site?.footer_cta_description) && (
        <div className="border-b" style={{ borderColor: dividerColor }}>
          <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 py-12 flex flex-wrap items-center justify-between gap-6">
            <div>
              {site?.footer_cta_heading && <h3 className="rebrand-serif text-xl sm:text-2xl font-semibold" style={{ color: "#1A202C" }}>{site.footer_cta_heading}</h3>}
              {site?.footer_cta_description && <p className="mt-1.5 text-sm" style={{ color: textColor, maxWidth: 480 }}>{site.footer_cta_description}</p>}
            </div>
            {site?.footer_cta_button_text && (
              <Link
                to={site.footer_cta_button_url || "/contact"}
                data-testid="footer-cta-button"
                className="inline-flex items-center gap-1.5 text-white text-sm font-semibold px-5 py-2.5 rounded-full transition-transform hover:scale-[1.02] shrink-0"
                style={{ backgroundColor: "#3B68E7" }}
              >
                {site.footer_cta_button_text} <ArrowUpRight className="w-3.5 h-3.5" />
              </Link>
            )}
          </div>
        </div>
      )}

      <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8" style={{ paddingTop: sectionSpacing, paddingBottom: 30 }}>
        {/* Main content grid — auto-fit handles any number of CMS-configured columns responsively */}
        <div className="grid gap-10" style={{ columnGap, rowGap: 40, gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))" }}>
          {/* Column 1 — Brand */}
          <div>
            <div className="flex items-center gap-2" style={{ justifyContent: footerLogoAlign }}>
              {footerLogoUrl ? (
                <>
                  <style>{`
                    @media(min-width: 640px) and (max-width: 1023px) {
                      .footer-logo-img { width: ${site?.footer_logo_tablet_width ?? footerLogoWidth}px !important; }
                    }
                    @media(max-width: 639px) {
                      .footer-logo-img { width: ${site?.footer_logo_mobile_width ?? footerLogoWidth}px !important; }
                    }
                  `}</style>
                  <img
                    src={footerLogoUrl.startsWith("http") ? footerLogoUrl : `${process.env.REACT_APP_BACKEND_URL}${footerLogoUrl}`}
                    alt={name}
                    className="footer-logo-img rounded object-cover shrink-0"
                    style={{
                      width: footerLogoWidth,
                      height: footerLogoHeight,
                      maxWidth: site?.footer_logo_max_width || undefined,
                      transform: site?.footer_logo_scale && site.footer_logo_scale !== 1 ? `scale(${site.footer_logo_scale})` : undefined,
                      transformOrigin: footerLogoAlign === "center" ? "center" : footerLogoAlign === "flex-end" ? "right" : "left",
                      marginTop: site?.footer_logo_margin_top || undefined,
                      marginRight: site?.footer_logo_margin_right || undefined,
                      marginBottom: site?.footer_logo_margin_bottom || undefined,
                      marginLeft: site?.footer_logo_margin_left || undefined,
                      paddingTop: site?.footer_logo_padding_top || undefined,
                      paddingRight: site?.footer_logo_padding_right || undefined,
                      paddingBottom: site?.footer_logo_padding_bottom || undefined,
                      paddingLeft: site?.footer_logo_padding_left || undefined,
                    }}
                    key={footerLogoUrl}
                  />
                </>
              ) : (
                <GraduationCap className="w-6 h-6 shrink-0" style={{ color: "#3B68E7" }} strokeWidth={2} aria-hidden="true" />
              )}
              <span className="font-bold uppercase tracking-wide" style={{ color: "#1A202C", fontSize: site?.footer_company_name_size ?? 18 }}>{name}</span>
            </div>
            <p className="mt-4 text-sm leading-relaxed" style={{ color: textColor, maxWidth: 320 }}>
              {description}
            </p>
            <div className="mt-5 space-y-2 text-sm" style={{ color: textColor }}>
              <a href={`mailto:${email}`} className="flex items-center gap-2 transition-colors duration-200" onMouseEnter={(e) => e.currentTarget.style.color = hoverColor} onMouseLeave={(e) => e.currentTarget.style.color = textColor}>
                <Mail className="w-3.5 h-3.5 shrink-0" aria-hidden="true" /> {email}
              </a>
              <a href={`tel:${phone.replace(/\s+/g, "")}`} className="flex items-center gap-2 transition-colors duration-200" onMouseEnter={(e) => e.currentTarget.style.color = hoverColor} onMouseLeave={(e) => e.currentTarget.style.color = textColor}>
                <Phone className="w-3.5 h-3.5 shrink-0" aria-hidden="true" /> {phone}
              </a>
              {whatsapp && (
                <a href={`https://wa.me/${whatsapp.replace(/[^\d]/g, "")}`} target="_blank" rel="noreferrer" className="flex items-center gap-2 transition-colors duration-200" onMouseEnter={(e) => e.currentTarget.style.color = "#25D366"} onMouseLeave={(e) => e.currentTarget.style.color = textColor} data-testid="footer-whatsapp">
                  <MessageCircle className="w-3.5 h-3.5 shrink-0" aria-hidden="true" /> {whatsapp}
                </a>
              )}
              <div className="flex items-center gap-2">
                <MapPin className="w-3.5 h-3.5 shrink-0" aria-hidden="true" /> {address}
              </div>
            </div>
          </div>

          {/* CMS-managed nav columns */}
          {columns.map((col) => (
            <nav key={col.title} aria-label={col.title}>
              <h3 className="text-[15px] font-semibold mb-4" style={{ color: "#1A202C" }}>{col.title}</h3>
              <ul className="space-y-3">
                {(col.links || []).filter((l) => l.visible !== false).map((l) => (
                  <li key={l.label}>
                    <Link
                      to={l.url}
                      className={linkCls}
                      style={linkStyle}
                      onMouseEnter={(e) => e.currentTarget.style.color = hoverColor}
                      onMouseLeave={(e) => e.currentTarget.style.color = linkColor}
                      data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>
          ))}

          {/* Follow Us */}
          <div>
            <h3 className="text-[15px] font-semibold mb-4" style={{ color: "#1A202C" }}>Follow Us</h3>
            <div className="flex flex-wrap gap-3">
              {socialIcons.map(([key, Icon]) =>
                social[key] ? (
                  <a
                    key={key}
                    href={social[key]}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={`${name} on ${key.charAt(0).toUpperCase()}${key.slice(1)}`}
                    data-testid={`footer-social-${key}`}
                    className="w-7 h-7 rounded-full grid place-items-center bg-[#1A202C] hover:bg-[#3B68E7] transition-all duration-[250ms] ease-out hover:scale-[1.08]"
                  >
                    <Icon className="w-3.5 h-3.5 text-white" aria-hidden="true" />
                  </a>
                ) : null
              )}
            </div>

            {site?.footer_newsletter_show && (
              <div className="mt-6">
                <h4 className="text-sm font-semibold" style={{ color: "#1A202C" }}>{site?.footer_newsletter_heading || "Subscribe to our newsletter"}</h4>
                {site?.footer_newsletter_description && <p className="mt-1 text-xs" style={{ color: textColor }}>{site.footer_newsletter_description}</p>}
              </div>
            )}
          </div>
        </div>

        {/* Divider */}
        <div style={{ height: 1, backgroundColor: dividerColor, marginTop: 50, marginBottom: 24 }} />

        {/* Bottom legal bar */}
        <div className="flex flex-col sm:flex-row items-center justify-center sm:justify-between gap-3 text-center sm:text-left">
          <span className="text-xs" style={{ color: textColor }}>
            {site?.footer_copyright_text || `© ${new Date().getFullYear()} ${name}. All Rights Reserved.`}
          </span>
          <div className="flex items-center gap-4 text-xs">
            <Link to={site?.footer_privacy_url || "/privacy"} style={linkStyle} className="hover:underline">Privacy Policy</Link>
            <Link to={site?.footer_terms_url || "/terms"} style={linkStyle} className="hover:underline">Terms</Link>
            <Link to="/podcast-terms" style={linkStyle} className="hover:underline">Podcast Terms</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

/* ─── WhatsApp Floating Button ───────────────────────────────────────────── */
/* ─── Floating Widgets Manager ──────────────────────────────────────────────
   Single container that positions and stacks every floating button on the
   site (WhatsApp, Prospectus, Back to Top, and future widgets like Chat or
   an AI Assistant). Each widget declares: position, order (lower = closer
   to the page edge), size, per-device visibility, and animation. The
   manager groups widgets by position bucket and stacks them automatically
   so they never overlap — admins only ever configure one widget at a time;
   the manager handles collision-free layout for all of them together. */

const FLOATING_ANIM_CSS = `
  @keyframes floatingPulse { 0%,100% { box-shadow: 0 0 0 0 rgba(79,70,229,0.5); } 70% { box-shadow: 0 0 0 14px rgba(79,70,229,0); } }
  @keyframes floatingBounce { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-8px); } }
  @keyframes floatingFloat { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-4px); } }
  @keyframes floatingGlow { 0%,100% { filter: drop-shadow(0 0 3px currentColor); } 50% { filter: drop-shadow(0 0 10px currentColor); } }
  @keyframes floatingSlideIn { from { transform: translateX(var(--slide-from, -12px)); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
  .floating-anim-pulse { animation: floatingPulse 2.2s ease-in-out infinite; }
  .floating-anim-bounce { animation: floatingBounce 1.6s ease-in-out infinite; }
  .floating-anim-float { animation: floatingFloat 3s ease-in-out infinite; }
  .floating-anim-glow { animation: floatingGlow 2s ease-in-out infinite; }
  .floating-anim-slide { animation: floatingSlideIn 0.4s ease-out; }
  @media (max-width: 767px) { .floating-hide-mobile { display: none !important; } }
  @media (min-width: 768px) and (max-width: 1023px) { .floating-hide-tablet { display: none !important; } }
  @media (min-width: 1024px) { .floating-hide-desktop { display: none !important; } }
`;

/** Groups widgets by position bucket and assigns a cumulative stack offset
 *  (in px) so buttons sharing a corner never overlap. Lower `order` sits
 *  closer to the page edge; later items stack outward from it. */
function computeFloatingStacks(widgets, gap) {
  const groups = {};
  widgets.forEach((w) => {
    if (!w.enabled) return;
    (groups[w.position] ||= []).push(w);
  });
  Object.values(groups).forEach((list) => {
    list.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    let cumulative = 0;
    list.forEach((w) => {
      w._stackOffset = cumulative;
      cumulative += (w.size || 56) + gap;
    });
  });
  return widgets;
}

/** Resolves a widget's position bucket + its computed stack offset into
 *  concrete fixed-position CSS. Bottom-anchored buckets stack upward
 *  (increase `bottom`); center buckets stack downward from the vertical
 *  midpoint (increase `top` and drop the translateY centering trick). */
function floatingPositionStyle(w) {
  const offset = w._stackOffset || 0;
  // Independent per-side offsets: distanceLeft/distanceRight/distanceTop/
  // distanceBottom are all stored in the CMS regardless of which position
  // bucket is active, so switching position never loses a previously-set
  // value. distanceSide is kept as a legacy fallback for callers (e.g.
  // WhatsApp) that haven't been migrated to the four independent fields.
  const left = w.distanceLeft ?? w.distanceSide;
  const right = w.distanceRight ?? w.distanceSide;
  switch (w.position) {
    case "left-center":
      return { left, top: `calc(50% + ${offset}px)`, transform: "translateY(-50%)" };
    case "right-center":
      return { right, top: `calc(50% + ${offset}px)`, transform: "translateY(-50%)" };
    case "left-top":
      return { left, top: (w.distanceTop || 0) + offset };
    case "right-top":
      return { right, top: (w.distanceTop || 0) + offset };
    case "right-bottom":
      return { right, bottom: (w.distanceBottom || 0) + offset };
    case "left-bottom":
    default:
      return { left, bottom: (w.distanceBottom || 0) + offset };
  }
}

function floatingVisibilityClass(w) {
  return [
    w.desktopVisible === false ? "floating-hide-desktop" : "",
    w.tabletVisible === false ? "floating-hide-tablet" : "",
    w.mobileVisible === false ? "floating-hide-mobile" : "",
  ].filter(Boolean).join(" ");
}

function floatingAnimClass(animation) {
  return animation && animation !== "none" ? `floating-anim-${animation}` : "";
}

/* ── WhatsApp button content — icon, chat link, click behaviour unchanged ── */
function WhatsAppButtonContent({ site }) {
  const whatsapp = site?.whatsapp || "";
  const number = whatsapp.replace(/[^\d]/g, "");
  const href = `https://wa.me/${number}`;
  const buttonSize = site?.whatsapp_button_size ?? 56;
  const iconSize = site?.whatsapp_icon_size ?? 22;
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      aria-label="Chat on WhatsApp"
      data-testid="whatsapp-floating-button"
      style={{
        backgroundColor: "#25D366",
        padding: `${Math.round(buttonSize * 0.2)}px ${Math.round(buttonSize * 0.32)}px ${Math.round(buttonSize * 0.2)}px ${Math.round(buttonSize * 0.25)}px`,
      }}
      className="flex items-center gap-2.5 w-full h-full"
    >
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="white" style={{ width: iconSize, height: iconSize }} className="shrink-0" aria-hidden="true">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 0 0-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
        <path d="M12 0C5.373 0 0 5.373 0 12c0 2.127.558 4.126 1.528 5.855L0 24l6.335-1.502A11.955 11.955 0 0 0 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.882a9.881 9.881 0 0 1-5.031-1.378l-.361-.214-3.741.981.999-3.648-.235-.374A9.852 9.852 0 0 1 2.118 12C2.118 6.538 6.538 2.118 12 2.118S21.882 6.538 21.882 12 17.462 21.882 12 21.882z" />
      </svg>
      <span className="text-white text-sm font-semibold whitespace-nowrap">WhatsApp</span>
    </a>
  );
}

/* ── Icon options CMS-selectable floating widgets can pick from ── */
export const FLOATING_ICON_MAP = {
  "file-down": FileDown,
  "download": Download,
  "file-text": FileText,
  "book-open": BookOpen,
  "graduation-cap": GraduationCap,
  "users": Users,
  "eye": Eye,
};

/* ── Prospectus button content + lead capture modal — unchanged logic ── */
function LeadCaptureModal({ onClose, onSubmit, submitting }) {
  const [form, setForm] = useState({ name: "", email: "", mobile: "", school: "", city: "" });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  useEffect(() => {
    const h = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.5)" }} onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-semibold text-gray-900">Get the Prospectus</h3>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-gray-100"><X className="w-4 h-4 text-gray-400" /></button>
        </div>
        <p className="text-xs text-gray-500 mb-4">Enter your details and the download will start automatically.</p>
        <div className="space-y-2.5">
          <input placeholder="Full Name *" value={form.name} onChange={(e) => set("name", e.target.value)}
            className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg outline-none focus:border-indigo-400" />
          <input type="email" placeholder="Email *" value={form.email} onChange={(e) => set("email", e.target.value)}
            className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg outline-none focus:border-indigo-400" />
          <input placeholder="Mobile" value={form.mobile} onChange={(e) => set("mobile", e.target.value)}
            className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg outline-none focus:border-indigo-400" />
          <input placeholder="School" value={form.school} onChange={(e) => set("school", e.target.value)}
            className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg outline-none focus:border-indigo-400" />
          <input placeholder="City" value={form.city} onChange={(e) => set("city", e.target.value)}
            className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg outline-none focus:border-indigo-400" />
        </div>
        <button
          onClick={() => onSubmit(form)}
          disabled={submitting || !form.name.trim() || !form.email.trim()}
          className="w-full mt-4 bg-[#0F172A] text-white text-sm font-semibold py-2.5 rounded-lg hover:bg-[#1e293b] transition disabled:opacity-50"
        >
          {submitting ? "Please wait…" : "Download Prospectus"}
        </button>
      </div>
    </div>
  );
}

function ProspectusButtonContent({ settings, location, onOpenLeadModal }) {
  const [submitting, setSubmitting] = useState(false);
  const pageSlug = location.pathname === "/" ? "home" : location.pathname.split("/")[1] || "home";

  async function triggerDownload(leadData = null) {
    try {
      if (leadData) {
        setSubmitting(true);
        await api.post("/prospectus/lead", { ...leadData, page: pageSlug });
      }
      const res = await fetch(`${BACKEND}/api/prospectus/download`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ page: pageSlug }),
      });
      if (!res.ok) throw new Error("download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = settings.pdf_filename || "prospectus.pdf";
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      onOpenLeadModal(false);
    } catch {
      // Widget failures must never break the page for the visitor
    } finally {
      setSubmitting(false);
    }
  }

  function handleClick() {
    if (settings.require_lead_capture) {
      onOpenLeadModal(true, () => triggerDownload);
    } else {
      triggerDownload();
    }
  }

  return (
    <button
      onClick={handleClick}
      title={settings.tooltip || "Download Prospectus"}
      aria-label={settings.button_label || "Download Prospectus"}
      data-testid="prospectus-floating-button"
      style={{
        background: settings.background || settings.button_color || "#4F46E5",
        padding: `${Math.round((settings.button_size || 56) * 0.2)}px ${Math.round((settings.button_size || 56) * 0.32)}px`,
      }}
      className="flex items-center gap-2.5 w-full h-full"
    >
      {(() => {
        const Icon = FLOATING_ICON_MAP[settings.icon] || FileDown;
        return <Icon style={{ width: settings.icon_size || 22, height: settings.icon_size || 22, color: settings.icon_color || "#FFFFFF" }} className="shrink-0" />;
      })()}
      <span className="text-sm font-semibold whitespace-nowrap" style={{ color: settings.icon_color || "#FFFFFF" }}>
        {settings.button_label || "Download Prospectus"}
      </span>
    </button>
  );
}

/* ── Back to Top button content — new, lightweight, scroll-triggered ── */
function BackToTopButtonContent({ site }) {
  return (
    <button
      onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
      aria-label="Back to top"
      data-testid="back-to-top-button"
      style={{
        background: site?.back_to_top_background || "#0F172A",
        width: "100%", height: "100%",
      }}
      className="flex items-center justify-center"
    >
      <ArrowUpRight
        style={{
          width: site?.back_to_top_icon_size || 18,
          height: site?.back_to_top_icon_size || 18,
          color: site?.back_to_top_icon_color || "#FFFFFF",
          transform: "rotate(-45deg)",
        }}
      />
    </button>
  );
}

/* ── Visitor Counter button content — click toggles live/total when the
   CMS display mode is "both"; otherwise it just displays the configured
   metric. Never navigates — the visitor stays on the current page. ── */
function VisitorCounterButtonContent({ settings, counts }) {
  const [showTotal, setShowTotal] = useState(settings.display_mode === "total");
  const Icon = FLOATING_ICON_MAP[settings.icon] || Users;
  const mode = settings.display_mode === "both" ? (showTotal ? "total" : "live") : settings.display_mode;
  const value = mode === "total" ? counts?.total : counts?.online;
  const label = mode === "total" ? (settings.label_total || "total visits") : (settings.label_live || "online now");

  function handleClick() {
    if (settings.display_mode === "both") setShowTotal((t) => !t);
    // No navigation and no page change for any display mode — the
    // counter only ever toggles its own display in place.
  }

  return (
    <button
      onClick={handleClick}
      title={`${value ?? 0} ${label}`}
      aria-label={`Visitor counter: ${value ?? 0} ${label}`}
      data-testid="visitor-counter-floating-button"
      style={{
        background: settings.background_color || "#0F172A",
        color: settings.text_color || "#FFFFFF",
        padding: `${Math.round((settings.size || 56) * 0.16)}px ${Math.round((settings.size || 56) * 0.28)}px`,
      }}
      className="flex items-center gap-2 w-full h-full"
    >
      <Icon style={{ width: (settings.size || 56) * 0.34, height: (settings.size || 56) * 0.34, color: settings.text_color || "#FFFFFF" }} className="shrink-0" />
      <span className="flex flex-col items-start leading-tight whitespace-nowrap">
        <span className="text-sm font-bold">{value ?? 0}</span>
        <span className="text-[10px] opacity-80">{label}</span>
      </span>
    </button>
  );
}

/* ── The unified manager ── */
export const FloatingWidgets = () => {
  const { site } = useCms();
  const location = useLocation();
  const [prospectus, setProspectus] = useState(null);
  const [visitorCounter, setVisitorCounter] = useState(null);
  const [visitorCounts, setVisitorCounts] = useState(null);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [leadModalOpen, setLeadModalOpen] = useState(false);
  const [pendingDownload, setPendingDownload] = useState(null);

  useEffect(() => {
    api.get("/prospectus/settings").then((r) => setProspectus(r.data)).catch(() => {});
    api.get("/visitor-counter/settings").then((r) => setVisitorCounter(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!visitorCounter?.enabled) return;
    const poll = () => api.get("/visitor-counter/count").then((r) => setVisitorCounts(r.data)).catch(() => {});
    poll();
    const interval = setInterval(poll, 30000);
    return () => clearInterval(interval);
  }, [visitorCounter?.enabled]);

  useEffect(() => {
    const onScroll = () => setShowBackToTop(window.scrollY > 400);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  if (!site) return null;

  const gap = site.floating_widgets_gap ?? 12;

  const widgets = [];

  if (site.whatsapp) {
    widgets.push({
      id: "whatsapp",
      enabled: true,
      position: site.whatsapp_position || "left-bottom",
      order: site.whatsapp_order ?? 2,
      size: site.whatsapp_button_size ?? 56,
      distanceBottom: site.whatsapp_distance_bottom ?? 24,
      distanceSide: site.whatsapp_distance_side ?? 24,
      zIndex: site.whatsapp_z_index ?? 55,
      borderRadius: site.whatsapp_border_radius ?? 9999,
      shadow: site.whatsapp_shadow !== false,
      animation: site.whatsapp_animation || "none",
      desktopVisible: site.whatsapp_desktop_visible !== false,
      tabletVisible: site.whatsapp_tablet_visible !== false,
      mobileVisible: site.whatsapp_mobile_visible !== false,
      render: () => <WhatsAppButtonContent site={site} />,
    });
  }

  if (prospectus?.enabled && prospectus?.pdf_url) {
    widgets.push({
      id: "prospectus",
      enabled: true,
      position: prospectus.position || "left-bottom",
      order: prospectus.order ?? 1,
      size: prospectus.button_size ?? 56,
      distanceTop: prospectus.distance_top ?? 24,
      distanceBottom: prospectus.distance_bottom ?? 24,
      distanceLeft: prospectus.distance_left ?? prospectus.distance_side ?? 24,
      distanceRight: prospectus.distance_right ?? prospectus.distance_side ?? 24,
      distanceSide: prospectus.distance_side ?? 24,
      zIndex: prospectus.z_index ?? 55,
      borderRadius: prospectus.border_radius ?? 9999,
      shadow: prospectus.shadow !== false,
      animation: prospectus.animation || "none",
      desktopVisible: prospectus.desktop_visible !== false,
      tabletVisible: prospectus.tablet_visible !== false,
      mobileVisible: prospectus.mobile_visible !== false,
      pageCheck: () => {
        const pageSlug = location.pathname === "/" ? "home" : location.pathname.split("/")[1] || "home";
        const showPages = prospectus.show_pages || ["all"];
        return showPages.includes("all") || showPages.includes(pageSlug);
      },
      render: () => (
        <ProspectusButtonContent
          settings={prospectus}
          location={location}
          onOpenLeadModal={(open, getDownloadFn) => {
            setLeadModalOpen(open);
            if (getDownloadFn) setPendingDownload(() => getDownloadFn());
          }}
        />
      ),
    });
  }

  if (visitorCounter?.enabled) {
    widgets.push({
      id: "visitor-counter",
      enabled: true,
      position: visitorCounter.position || "right-bottom",
      order: visitorCounter.order ?? 3,
      size: visitorCounter.size ?? 56,
      distanceTop: visitorCounter.distance_top ?? 24,
      distanceBottom: visitorCounter.distance_bottom ?? 24,
      distanceLeft: visitorCounter.distance_left ?? 24,
      distanceRight: visitorCounter.distance_right ?? 24,
      zIndex: visitorCounter.z_index ?? 55,
      borderRadius: visitorCounter.border_radius ?? 9999,
      shadow: visitorCounter.shadow !== false,
      animation: "none",
      desktopVisible: visitorCounter.desktop_visible !== false,
      tabletVisible: visitorCounter.tablet_visible !== false,
      mobileVisible: visitorCounter.mobile_visible !== false,
      render: () => <VisitorCounterButtonContent settings={visitorCounter} counts={visitorCounts} />,
    });
  }

  if (site.back_to_top_enabled !== false && showBackToTop) {
    widgets.push({
      id: "back-to-top",
      enabled: true,
      position: site.back_to_top_position || "right-bottom",
      order: site.back_to_top_order ?? 1,
      size: site.back_to_top_size ?? 44,
      distanceBottom: site.back_to_top_position?.includes("bottom") ? (site.whatsapp_distance_bottom ?? 24) : 0,
      distanceSide: site.whatsapp_distance_side ?? 24,
      zIndex: site.back_to_top_z_index ?? 55,
      borderRadius: site.back_to_top_border_radius ?? 9999,
      shadow: site.back_to_top_shadow !== false,
      animation: site.back_to_top_animation || "none",
      desktopVisible: site.back_to_top_desktop_visible !== false,
      tabletVisible: site.back_to_top_tablet_visible !== false,
      mobileVisible: site.back_to_top_mobile_visible !== false,
      render: () => <BackToTopButtonContent site={site} />,
    });
  }

  // Filter widgets that fail their own visibility rules (e.g. prospectus page check)
  const visibleWidgets = widgets.filter((w) => !w.pageCheck || w.pageCheck());
  computeFloatingStacks(visibleWidgets, gap);

  return (
    <>
      <style>{FLOATING_ANIM_CSS}</style>
      {visibleWidgets.map((w) => (
        <div
          key={w.id}
          className={`fixed transition-all duration-200 hover:scale-[1.05] active:scale-[0.97] overflow-hidden ${floatingVisibilityClass(w)} ${floatingAnimClass(w.animation)}`}
          style={{
            ...floatingPositionStyle(w),
            zIndex: w.zIndex ?? 55,
            borderRadius: `${w.borderRadius}px`,
            boxShadow: w.shadow ? "0 10px 25px -5px rgba(0,0,0,0.25)" : "none",
            // WhatsApp's button size must be a hard height, not a minimum — minHeight
            // can only grow the box, never shrink it below its content's natural
            // height, so reducing the CMS value had no visible effect. Scoped to
            // WhatsApp only so Prospectus/Back-to-Top's own sizing is unaffected.
            ...(w.id === "whatsapp" ? { height: w.size } : { minHeight: w.size }),
          }}
        >
          {w.render()}
        </div>
      ))}

      {leadModalOpen && (
        <LeadCaptureModal
          onClose={() => setLeadModalOpen(false)}
          onSubmit={(form) => pendingDownload && pendingDownload(form)}
          submitting={false}
        />
      )}
    </>
  );
};

/* ── Backward-compatible standalone exports (self-positioning, unstacked) ──
   Kept for any code path that might still import these directly. The single
   source of truth for the live site is <FloatingWidgets /> above, rendered
   once from MarketingLayout. */
export const WhatsAppFloatingButton = () => {
  const { site } = useCms();
  if (!site?.whatsapp) return null;
  return (
    <div className="fixed bottom-6 left-6 z-50 rounded-full shadow-lg hover:shadow-xl transition-all">
      <WhatsAppButtonContent site={site} />
    </div>
  );
};

export const ProspectusFloatingButton = () => {
  const location = useLocation();
  const [settings, setSettings] = useState(null);
  useEffect(() => { api.get("/prospectus/settings").then((r) => setSettings(r.data)).catch(() => {}); }, []);
  if (!settings?.enabled || !settings?.pdf_url) return null;
  return (
    <div className="fixed bottom-6 left-24 z-[55] rounded-full shadow-lg hover:shadow-xl transition-all">
      <ProspectusButtonContent settings={settings} location={location} onOpenLeadModal={() => {}} />
    </div>
  );
};